import { Box, Button, Flex, ListItem, OrderedList, Stack, Text } from '@chakra-ui/react'
import React, { useCallback, useMemo } from 'react'

import { getConfirmationFormInput } from '@/client/services/formInputs'

import Form from '@/components/UI/Form/Form'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import useRequestNotificationsModal from '@/components/Worlds/Notification/useRequestNotificationsModal'

const WHAT_TO_EXPECT = [
  {
    title: 'Once you join, your turn for 1:1 help will come soon.',
    description:
      `If a tutor is available to help, you'll be able to join their Zoom room. ` +
      `Otherwise, keep this page open and you'll get a notification when a tutor is free.`,
  },
  {
    title: 'Your tutor will help with -- not solve -- your homework problems.',
    description:
      'They will help you break down your problems or walk you through a similar problem. ' +
      `We value learning—don't expect to simply get the answer!`,
  },
]

export default function LearnerRequestLiveHelpModal({
  open,
  onClose,
  onSubmit,
  isLoading = false,
}: {
  open: boolean
  onClose: () => void
  onSubmit: () => Promise<void>
  isLoading?: boolean
}): JSX.Element {
  const { showRequestNotificationsModal, RequestNotificationsModal } = useRequestNotificationsModal(
    {
      body: 'We recommend turning on notifications so we can let you know as soon as a tutor is ready to help you!',
    }
  )
  const [confirmationFormInput, validateConfirmationFormInput] = React.useMemo(
    () =>
      getConfirmationFormInput(
        'Please confirm that you understand the expectations',
        'I understand'
      ),
    []
  )

  const handleSubmit = useCallback(() => {
    onClose()
    showRequestNotificationsModal({
      onComplete: onSubmit,
    })
  }, [onClose, onSubmit, showRequestNotificationsModal])

  const formInputs = useMemo(() => [confirmationFormInput], [confirmationFormInput])

  const liveHelpRegisterFooter = () => (
    <Flex mt={4} justify="end">
      <Button variant="outline" onClick={onClose} mr={2} isDisabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" isLoading={isLoading}>
        Next
      </Button>
    </Flex>
  )

  return (
    <>
      <ModalV3
        size="2xl"
        open={open}
        onClose={onClose}
        title="Request Live Help"
        body={() => (
          <Stack direction="column" spacing="16px">
            <Box bgColor="bg.subtle" p={4}>
              <Text variant="eyebrow">Expectations</Text>
              <OrderedList p={0}>
                {WHAT_TO_EXPECT.map(({ title, description }) => (
                  <ListItem key={title} mb={3}>
                    <Text fontWeight="bold">{title}</Text>
                    <Text>{description}</Text>
                  </ListItem>
                ))}
              </OrderedList>
            </Box>
            <Form
              name="LiveHelpJoinModal"
              blocks={formInputs}
              onSubmit={handleSubmit}
              onValidate={validateConfirmationFormInput}
              FooterComponent={liveHelpRegisterFooter}
            />
          </Stack>
        )}
      />
      <RequestNotificationsModal />
    </>
  )
}
